import ActionsForm from "./ActionsForm";
import ActionForm from "@serie3/freelancesplaceapi/Form/Crm/ActionForm";
import NewModal from "../../../documents/modals/NewModal";
import { useFormik } from "formik";
import SaveButton from "../../../../../layout/Buttons/SaveButton";
import { useEffect, useState, useRef } from "react";
import Action from "@serie3/freelancesplaceapi/Entity/Crm/Action";
import getSettings from "@serie3/common/config/settings";
import PaymentModal from "../../../../../layout/Payment/PaymentModal";
import crudApi from "@serie3/common/api/crudApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagicWandSparkles, faListCheck, faXmark, faPlus } from "@fortawesome/free-solid-svg-icons";
import FilePreviewPanel from "../../../../../layout/Documents/FilePreviewPanel";

const spinCss = `@keyframes ai-spin { to { transform: rotate(360deg); } }`;

const JsonViewer = ({ data }) => {
  if (data === null || data === undefined) return null;

  const renderValue = (val, depth = 0) => {
    if (val === null) return <span style={{ color: "#9e9e9e" }}>null</span>;
    if (typeof val === "boolean") return <span style={{ color: "#a855f7" }}>{val.toString()}</span>;
    if (typeof val === "number") return <span style={{ color: "#f59e0b" }}>{val}</span>;
    if (typeof val === "string") return <span style={{ color: "#10b981" }}>"{val}"</span>;

    if (Array.isArray(val)) {
      if (val.length === 0) return <span style={{ color: "#9e9e9e" }}>[]</span>;
      return (
        <div style={{ marginLeft: depth > 0 ? "16px" : 0 }}>
          {val.map((item, i) => (
            <div key={i} style={{ marginBottom: "4px" }}>
              <span style={{ color: "#9e9e9e", fontSize: "0.7rem", marginRight: "6px" }}>{i}</span>
              {renderValue(item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof val === "object") {
      return (
        <div style={{ marginLeft: depth > 0 ? "16px" : 0 }}>
          {Object.entries(val).map(([k, v]) => (
            <div key={k} style={{ marginBottom: "6px" }}>
              <span style={{ color: "#60a5fa", fontWeight: 600 }}>{k}</span>
              <span style={{ color: "#9e9e9e" }}>: </span>
              {renderValue(v, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(val)}</span>;
  };

  return (
    <div style={{
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
      fontSize: "0.8rem",
      lineHeight: 1.7,
      color: "#e2e8f0",
    }}>
      {renderValue(data)}
    </div>
  );
};

const TaskCard = ({ task, onCreate }) => {
  return (
    <div style={{ borderLeft: "4px solid #1a73e8", borderRadius: "8px", padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121", marginBottom: "4px" }}>
            {task.title}
          </div>
          {task.reason && (
            <div style={{ fontSize: "0.8rem", color: "#757575", lineHeight: 1.5 }}>
              {task.reason}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onCreate(task)}
          title="Create ticket"
          style={{
            flexShrink: 0,
            padding: "5px 10px", borderRadius: "6px", border: "none",
            background: "#e8f0fe", color: "#1a73e8",
            fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: "5px",
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          Create
        </button>
      </div>
    </div>
  );
};

const ActionsFormWrapper = ({
  showForm,
  setShowForm,
  editAction,
  setEditAction,
  tickets,
  setTickets,
  inactiveTickets,
  setInactiveTickets,
  projectFormik,
  projectId,
}) => {
  const [showError, setShowError] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [settings, setSettings] = useState();
  const [saving, setSaving] = useState(false);
  const [aiPanel, setAiPanel] = useState({ type: null, loading: false, data: null });
  const [filePreview, setFilePreview] = useState(null);
  const autosaveReady = useRef(false);

  const editActionForm = ActionForm.createActionForm(
    tickets,
    setTickets,
    setShowError,
    setShowPayModal,
    setEditAction,
  );

  useEffect(() => {
    setSettings(getSettings());
  }, []);

  const formik = useFormik(editActionForm);

  useEffect(() => {
    autosaveReady.current = false;
    if (showForm && editAction?.id) {
      const t = setTimeout(() => { autosaveReady.current = true; }, 600);
      return () => clearTimeout(t);
    }
  }, [showForm, editAction?.id]);

  // Reset panels when modal closes; sync board with final form state
  useEffect(() => {
    if (!showForm) {
      if (editAction?.id) {
        const updated = { ...editAction, ...formik.values };
        syncTicketToBoard(updated);
      }
      setAiPanel({ type: null, loading: false, data: null });
      setFilePreview(null);
    }
  }, [showForm]);

  const formikValuesRef = useRef(formik.values);
  useEffect(() => { formikValuesRef.current = formik.values; });

  useEffect(() => {
    if (!editAction?.id || !autosaveReady.current) return;
    const t = setTimeout(async () => {
      setSaving(true);
      const payload = JSON.stringify({ ...formikValuesRef.current, id: editAction.id });
      await crudApi.edit("/crm/tickets/edit", payload, true);
      setSaving(false);
    }, 1500);
    return () => clearTimeout(t);
  }, [formik.values]);

  const callAi = async (type) => {
    if (!editAction?.id) return;
    setFilePreview(null);
    setAiPanel({ type, loading: true, data: null });
    const endpoint = type === "clarify"
      ? `/crm/tickets/clarify/${editAction.id}`
      : `/crm/tickets/followUpTasks/${editAction.id}`;
    const payload = JSON.stringify({ projectDescription: projectFormik?.values?.description || "" });
    const res = await crudApi.createNew(endpoint, payload, null, true);
    setAiPanel({ type, loading: false, data: res.data ?? null });
  };

  const isInactive = (status) => status === "Done" || status === "Canceled";

  const syncTicketToBoard = (updatedAction) => {
    const nowInactive = isInactive(updatedAction.status);
    const wasInactive = inactiveTickets?.some((t) => t.id === updatedAction.id);

    if (nowInactive) {
      // remove from active, upsert into inactive
      setTickets((prev) => prev.filter((t) => t.id !== updatedAction.id));
      if (setInactiveTickets) {
        setInactiveTickets((prev) =>
          prev.some((t) => t.id === updatedAction.id)
            ? prev.map((t) => t.id === updatedAction.id ? updatedAction : t)
            : [...prev, updatedAction]
        );
      }
    } else {
      // remove from inactive, upsert into active
      if (setInactiveTickets) {
        setInactiveTickets((prev) => prev.filter((t) => t.id !== updatedAction.id));
      }
      if (wasInactive) {
        setTickets((prev) => [...prev, updatedAction]);
      } else {
        setTickets((prev) => prev.map((t) => t.id === updatedAction.id ? updatedAction : t));
      }
    }
  };

  const handleClick = async (e) => {
    e.preventDefault();
    if (editAction === null) {
      if (Object.keys(formik.errors).length === 0) {
        projectFormik.setFieldValue("action", formik.values);
        projectFormik.setFieldValue("project", { id: projectId });
        projectFormik.handleSubmit();
      }
    } else {
      const values = { ...formik.values, id: editAction.id };
      const response = await crudApi.edit("/crm/tickets/edit", JSON.stringify(values));
      if (response.status === 200) {
        const updated = { ...editAction, ...formik.values };
        setEditAction(updated);
        syncTicketToBoard(updated);
      }
    }
  };

  const createFromTask = async (task) => {
    // Save current ticket first
    if (editAction?.id) {
      await crudApi.edit("/crm/tickets/edit", JSON.stringify({ ...formik.values, id: editAction.id }), true);
    }
    // Switch to new ticket mode pre-filled with task values
    const today = new Date().toISOString().split("T")[0];
    setEditAction(null);
    setAiPanel({ type: null, loading: false, data: null });
    formik.setValues({
      ...Action,
      tags: [],
      title: task.title || "",
      beschreibung: task.reason || "",
      start: today,
    });
  };

  const panelOpen = !!aiPanel.type || !!filePreview;

  const AiButtons = () => (
    <div style={{ display: "flex", gap: "8px" }}>
      <style>{spinCss}</style>
      <button
        type="button"
        onClick={() => callAi("clarify")}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          padding: "7px 13px", border: "none", borderRadius: "8px",
          fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
          background: aiPanel.type === "clarify" ? "#1a73e8" : "#e8f0fe",
          color: aiPanel.type === "clarify" ? "#fff" : "#1a73e8",
          transition: "all 0.15s",
        }}
      >
        <FontAwesomeIcon icon={faMagicWandSparkles} />
        Clarify
      </button>
      <button
        type="button"
        onClick={() => callAi("followUp")}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          padding: "7px 13px", border: "none", borderRadius: "8px",
          fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
          background: aiPanel.type === "followUp" ? "#1a73e8" : "#e8f0fe",
          color: aiPanel.type === "followUp" ? "#fff" : "#1a73e8",
          transition: "all 0.15s",
        }}
      >
        <FontAwesomeIcon icon={faListCheck} />
        Follow-up Tasks
      </button>
    </div>
  );

  const editTitle = editAction ? editAction.title : "Create Ticket";

  const setFormikNewValues = () => {
    formik.setValues(Action);
    formik.setFieldValue("tags", []);
  };

  return (
    <>
      <style>{`
        .modal-ai-wide { max-width: min(95vw, 1300px) !important; }
        .modal-90w { max-width: min(95vw, 900px) !important; }
      `}</style>

      {settings && (
        <PaymentModal
          show={showPayModal}
          setShow={setShowPayModal}
          priceTableId={settings.pricingTables.document}
        />
      )}

      <NewModal
        show={showForm}
        setShow={setShowForm}
        ExtraButton={() => <SaveButton handleClick={handleClick} isSaving={saving} />}
        title={editTitle}
        modalType={panelOpen ? "modal-ai-wide" : "modal-90w"}
        onShow={() => {
          editAction
            ? formik.setValues(ActionForm.setEditData(editAction))
            : setFormikNewValues();
        }}
      >
        <div style={{ display: "flex", gap: "0", minHeight: 0, alignItems: "stretch" }}>
          {/* Form — always visible */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <ActionsForm
              formik={formik}
              editAction={editAction}
              setShowPayModal={setShowPayModal}
              AiButtons={editAction?.id ? AiButtons : undefined}
              onFileSelect={(f) => { setFilePreview(f); if (f) setAiPanel({ type: null, loading: false, data: null }); }}
              selectedFileId={filePreview?.id}
            />
          </div>

          {/* File preview panel */}
          {filePreview && (
            <FilePreviewPanel
              attachment={filePreview}
              onClose={() => setFilePreview(null)}
            />
          )}

          {/* AI panel — slides in from the right */}
          {!!aiPanel.type && (
            <div style={{
              width: "380px",
              flexShrink: 0,
              marginLeft: "20px",
              borderLeft: "1px solid #e0e0e0",
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              minHeight: 0,
              overflow: "hidden",
            }}>
              {/* Panel header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                  <div style={{
                    width: "26px", height: "26px", borderRadius: "7px",
                    background: "linear-gradient(135deg, #1a73e8, #0d47a1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <FontAwesomeIcon icon={aiPanel.type === "clarify" ? faMagicWandSparkles : faListCheck} style={{ fontSize: "0.7rem", color: "#fff" }} />
                  </div>
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121" }}>
                    {aiPanel.type === "clarify" ? "Clarification" : "Follow-up Tasks"}
                  </span>
                </div>
                <button
                  onClick={() => setAiPanel({ type: null, loading: false, data: null })}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#9e9e9e", padding: "4px" }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              {/* Result */}
              {aiPanel.loading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "14px", color: "#9e9e9e" }}>
                  <div style={{
                    width: "36px", height: "36px", borderRadius: "50%",
                    border: "3px solid #e8f0fe",
                    borderTopColor: "#1a73e8",
                    animation: "ai-spin 0.8s linear infinite",
                  }} />
                  <style>{`@keyframes ai-spin { to { transform: rotate(360deg); } }`}</style>
                  <span style={{ fontSize: "0.82rem" }}>Thinking…</span>
                </div>
              )}
              {!aiPanel.loading && aiPanel.data !== null && (
                aiPanel.type === "followUp" && Array.isArray(aiPanel.data?.tasks) ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflowY: "auto", flex: 1 }}>
                    {aiPanel.data.tasks.map((task, i) => (
                      <TaskCard
                        key={i}
                        task={task}
                        onCreate={createFromTask}
                      />
                    ))}
                  </div>
                ) : aiPanel.type === "clarify" && aiPanel.data?.issues ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto", flex: 1 }}>
                    {/* Status badge */}
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      padding: "5px 12px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 700,
                      background: aiPanel.data.is_clear ? "#e6f4ea" : "#fce8e6",
                      color: aiPanel.data.is_clear ? "#1e7e34" : "#c62828",
                      alignSelf: "flex-start",
                    }}>
                      {aiPanel.data.is_clear ? "✓ Clear" : "✗ Needs clarification"}
                    </div>

                    {/* Updated title preview */}
                    {aiPanel.data.updated_title && (
                      <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#424242", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Improved title</div>
                        <div style={{ fontSize: "0.85rem", color: "#212121" }}>{aiPanel.data.updated_title}</div>
                      </div>
                    )}

                    {/* Updated description preview */}
                    {aiPanel.data.updated_description && (
                      <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#424242", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Improved description</div>
                        <div style={{ fontSize: "0.82rem", color: "#424242", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{aiPanel.data.updated_description}</div>
                      </div>
                    )}

                    {/* Apply button */}
                    {(aiPanel.data.updated_title || aiPanel.data.updated_description) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (aiPanel.data.updated_title) formik.setFieldValue("title", aiPanel.data.updated_title);
                          if (aiPanel.data.updated_description) formik.setFieldValue("beschreibung", aiPanel.data.updated_description);
                        }}
                        style={{
                          padding: "8px 14px", borderRadius: "8px",
                          border: "none", background: "#1a73e8", color: "#fff",
                          fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        Apply to ticket
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{
                    background: "#1a1e2e",
                    borderRadius: "10px",
                    padding: "16px",
                    overflowY: "auto",
                    flex: 1,
                  }}>
                    <JsonViewer data={aiPanel.data} />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </NewModal>
    </>
  );
};

export default ActionsFormWrapper;
